import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, ExternalLink, KeyRound, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { create } from "zustand";
import { ClaudeMark } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/overlay";
import { Input, Spinner } from "@/components/ui/primitives";
import { desktop } from "@/desktop/bridge";
import { cn } from "@/lib/utils";

/**
 * Entrar com a conta da Anthropic: o navegador abre o console da Anthropic, a pessoa entra e cria
 * uma chave para o Kompil; ao voltar, o Kompil reconhece a chave copiada e pede a autorização dela
 * antes de conectar. A chave fica cifrada no cofre do sistema e as chamadas saem deste computador.
 * (Assinaturas Claude Pro/Max valem só nos apps da própria Anthropic — a política dela não permite
 * que outros apps usem o login do claude.ai.)
 */
const ANTHROPIC_KEYS_URL = "https://console.anthropic.com/settings/keys";
const KEY = /\bsk-ant-[A-Za-z0-9_-]{20,300}\b/;

export const useClaudeConnect = create<{ open: boolean }>(() => ({ open: false }));
export const openClaudeConnect = () => useClaudeConnect.setState({ open: true });

/** Diálogo global (canto, barra de status, painel): o mesmo fluxo de entrar e autorizar. */
export function ClaudeConnectDialog() {
  const open = useClaudeConnect((s) => s.open);
  const close = () => useClaudeConnect.setState({ open: false });
  if (!desktop) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : close())}>
      <DialogContent
        title="Entrar com a Anthropic"
        description="Use o Claude com a sua conta da API da Anthropic: modelos, limites e cobrança da sua conta."
      >
        <ConnectAnthropic onDone={close} compact />
      </DialogContent>
    </Dialog>
  );
}

const mask = (key: string) => `${key.slice(0, 13)}…${key.slice(-4)}`;

export function ConnectAnthropic({ onDone, compact }: { onDone?: () => void; compact?: boolean }) {
  const qc = useQueryClient();
  const [step, setStep] = useState<"start" | "waiting" | "authorize">("start");
  const [found, setFound] = useState("");
  const [typed, setTyped] = useState("");
  const authorize = useMutation({
    mutationFn: (key: string) => desktop!.claude.connect(key),
    onSuccess: (r) => {
      if (!r.ok) {
        setStep("waiting");
        return void toast.error(r.error ?? "Não foi possível conectar.");
      }
      // A chave não fica esquecida na área de transferência.
      void navigator.clipboard?.writeText("").catch(() => {});
      setFound("");
      setTyped("");
      setStep("start");
      toast.success("Claude conectado à sua conta Anthropic.");
      void qc.invalidateQueries({ queryKey: ["claude", "account"] });
      onDone?.();
    },
  });

  // Esperando a chave: confere a área de transferência ao voltar para o app (só procura uma chave).
  useEffect(() => {
    if (step !== "waiting") return;
    const check = async () => {
      if (!document.hasFocus()) return;
      try {
        const text = await navigator.clipboard.readText();
        const key = KEY.exec(text)?.[0];
        if (key) {
          setFound(key);
          setStep("authorize");
        }
      } catch {
        // sem permissão de leitura: a pessoa cola no campo
      }
    };
    void check();
    const t = setInterval(() => void check(), 1_200);
    window.addEventListener("focus", check);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", check);
    };
  }, [step]);

  const openBrowser = () => {
    desktop?.openExternal(ANTHROPIC_KEYS_URL);
    setStep("waiting");
  };
  const pasted = KEY.exec(typed.trim())?.[0];

  if (step === "authorize" && found) {
    return (
      <div className={cn("w-full space-y-3 text-left", !compact && "max-w-[320px]")}>
        <div className="flex items-center gap-3 rounded-xl border border-border-strong bg-bg-subtle p-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border bg-bg">
            <ClaudeMark className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-fg">Autorizar o Kompil a usar o Claude?</p>
            <p className="truncate font-mono text-[11.5px] text-fg-subtle">{mask(found)}</p>
          </div>
        </div>
        <ul className="space-y-1.5 text-[12px] leading-snug text-fg-muted">
          {[
            "Conversar com o Claude e usar o agente nos projetos com a IA ligada.",
            "O uso é cobrado na sua conta da Anthropic; o Kompil mostra quanto cada coisa consome.",
            "A chave fica cifrada no cofre deste computador e nunca vai para o servidor do Kompil.",
            "Você desconecta quando quiser (e pode revogar a chave no console).",
          ].map((t) => (
            <li key={t} className="flex gap-2">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fg" /> {t}
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <Button
            variant="primary"
            className="flex-1"
            loading={authorize.isPending}
            onClick={() => authorize.mutate(found)}
          >
            <ShieldCheck className="h-3.5 w-3.5" /> Autorizar
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setFound("");
              setStep("start");
            }}
          >
            Não autorizar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("w-full space-y-3 text-left", !compact && "max-w-[320px]")}>
      <Button variant="primary" className="w-full" onClick={openBrowser}>
        <ClaudeMark className="h-3.5 w-3.5" mono />{" "}
        {step === "waiting" ? "Abrir o navegador de novo" : "Entrar com a Anthropic"}
      </Button>
      {step === "waiting" ? (
        <div className="space-y-2.5 rounded-xl border border-border bg-bg-subtle p-3">
          <ol className="space-y-1.5 text-[12px] leading-snug text-fg-muted">
            <li>1. No navegador, entre na sua conta da Anthropic.</li>
            <li>
              2. Clique em <span className="font-medium text-fg">Create Key</span>, dê o nome{" "}
              <span className="font-medium text-fg">Kompil</span> e copie a chave.
            </li>
            <li>3. Volte para cá: o Kompil reconhece a chave e pede a sua autorização.</li>
          </ol>
          <div className="flex items-center gap-2 text-[12px] text-fg-subtle">
            <Spinner className="h-3.5 w-3.5" /> Aguardando a chave copiada…
          </div>
        </div>
      ) : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!pasted) return;
          setFound(pasted);
          setStep("authorize");
        }}
        className="flex gap-2"
      >
        <Input
          type="password"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="ou cole a chave: sk-ant-…"
          autoComplete="off"
          spellCheck={false}
          aria-label="Chave da API da Anthropic"
        />
        <Button type="submit" variant="secondary" disabled={!pasted} aria-label="Usar esta chave">
          <KeyRound className="h-3.5 w-3.5" />
        </Button>
      </form>
      <p className="text-[11.5px] leading-relaxed text-fg-subtle">
        A conta é a da API da Anthropic (console). Assinaturas Claude Pro/Max valem só nos apps da própria
        Anthropic, que não permite login do claude.ai em outros apps.{" "}
        <button
          type="button"
          onClick={() => desktop?.openExternal(ANTHROPIC_KEYS_URL)}
          className="inline-flex items-center gap-0.5 underline underline-offset-2 hover:text-fg"
        >
          Console <ExternalLink className="h-3 w-3" />
        </button>
      </p>
    </div>
  );
}
